import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collaborationAPI } from '../../api/collaboration';
import type { CollaboratorInfo, InvitationRequest } from '../../types/collaboration';
import { hasPermission } from '../../types/collaboration';
import './CollaborationPanel.css';

interface CollaborationPanelProps {
  examinationId: string;
  userRole: 'owner' | 'collaborator' | 'viewer';
  onCollaboratorsChange?: () => void;
}

export const CollaborationPanel: React.FC<CollaborationPanelProps> = ({
  examinationId,
  userRole,
  onCollaboratorsChange,
}) => {
  const { t } = useTranslation();
  const [collaborators, setCollaborators] = useState<CollaboratorInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState<InvitationRequest>({
    email: '',
    role: 'viewer',
  });
  const [inviting, setInviting] = useState(false);

  // 共同作業者一覧を読み込み
  const loadCollaborators = async () => {
    try {
      setLoading(true);
      const data = await collaborationAPI.getCollaborators(examinationId);
      setCollaborators(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '共同作業者の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollaborators();
  }, [examinationId]);

  // 招待送信
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;

    try {
      setInviting(true);
      await collaborationAPI.inviteCollaborator(examinationId, inviteForm);
      setInviteForm({ email: '', role: 'viewer' });
      setShowInviteForm(false);
      await loadCollaborators();
      onCollaboratorsChange?.();
      alert(t('collaboration.success.invitationSent'));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('collaboration.error.invitationFailed'));
    } finally {
      setInviting(false);
    }
  };

  // 共同作業者削除
  const handleRemoveCollaborator = async (userId: string, userName: string) => {
    if (!confirm(`${userName}を共同作業者から削除しますか？`)) return;

    try {
      await collaborationAPI.removeCollaborator(examinationId, userId);
      await loadCollaborators();
      onCollaboratorsChange?.();
      alert(t('collaboration.success.collaboratorRemoved'));
    } catch (err) {
      alert(err instanceof Error ? err.message : t('collaboration.error.removalFailed'));
    }
  };

  const canInviteUsers = hasPermission(userRole, 'canInviteUsers');
  const canRemoveUsers = hasPermission(userRole, 'canRemoveUsers');

  if (loading) {
    return <div className="collaboration-panel loading">読み込み中...</div>;
  }

  return (
    <div className="collaboration-panel">
      <div className="collaboration-header">
        <h3>共同作業者</h3>
        {canInviteUsers && (
          <button
            className="btn-invite"
            onClick={() => setShowInviteForm(true)}
            disabled={showInviteForm}
          >
            招待
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showInviteForm && (
        <form className="invite-form" onSubmit={handleInvite}>
          <div className="form-group">
            <label htmlFor="email">メールアドレス</label>
            <input
              id="email"
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="example@university.edu"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="role">役割</label>
            <select
              id="role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as any })}
            >
              <option value="viewer">閲覧者</option>
              <option value="collaborator">共同作業者</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={inviting}>
              {inviting ? '送信中...' : '招待送信'}
            </button>
            <button
              type="button"
              onClick={() => setShowInviteForm(false)}
              disabled={inviting}
            >
              キャンセル
            </button>
          </div>
        </form>
      )}

      <div className="collaborators-list">
        {collaborators.length === 0 ? (
          <p className="no-collaborators">共同作業者はいません</p>
        ) : (
          <ul>
            {collaborators.map((collaborator) => (
              <li key={collaborator.userId} className="collaborator-item">
                <div className="collaborator-info">
                  <div className="collaborator-name">{collaborator.fullName}</div>
                  <div className="collaborator-email">{collaborator.email}</div>
                  <div className="collaborator-role">
                    <span className={`role-badge role-${collaborator.role}`}>
                      {collaborator.role === 'owner' ? '所有者' :
                       collaborator.role === 'collaborator' ? '共同作業者' : '閲覧者'}
                    </span>
                    {collaborator.status === 'pending' && (
                      <span className="status-badge status-pending">招待中</span>
                    )}
                  </div>
                </div>
                {canRemoveUsers && collaborator.role !== 'owner' && (
                  <button
                    className="btn-remove"
                    onClick={() => handleRemoveCollaborator(collaborator.userId, collaborator.fullName)}
                    title="削除"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
